const RG_ALLOWED_CHARS_REGEX = /^[\d.\-\w\s]+$/;
const CPF_REGEX = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/;
const DATE_REGEX = /(^(((0[1-9]|1[0-9]|2[0-8])[/](0[1-9]|1[012]))|((29|30|31)[/](0[13578]|1[02]))|((29|30)[/](0[4,6,9]|11)))[/](19|[2-9][0-9])\d\d$)|(^29[/]02[/](19|[2-9][0-9])(00|04|08|12|16|20|24|28|32|36|40|44|48|52|56|60|64|68|72|76|80|84|88|92|96)$)/;
const EMAIL_REGEX = /[A-Z0-9a-z._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;
const MOBILE_PHONE_REGEX = /\(\d{2}\)\s\d{5}-\d{4}/;
export const ONLY_LETTERS = /[^áâãéêíîóôõúûÁÂÃÉÊÍÎÓÔÕÚÛa-z ]/gi;

export const BRAZILIAN_UFS = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
];

export function isRgValid(rg: string): boolean {
  return RG_ALLOWED_CHARS_REGEX.test(rg);
}

export function isUfValid(uf: string): boolean {
  return BRAZILIAN_UFS.includes((uf || '').trim().toUpperCase());
}

/**
 * A UF do órgão expedidor precisa ser uma sigla de 2 letras: a coluna
 * `uf_orgao_expedidor` do JACAD tem esse tamanho, e valor maior derruba a matrícula
 * com um 422 de `Data truncation` que chega ao lead como "Tivemos um problema".
 *
 * Valida contra as opções recebidas do backend, que são a fonte de verdade do que o
 * JACAD aceita. Mas o getter devolve `[]` quando o PULL_ISSUE_STATE_OPTIONS falha —
 * nesse caso cair para a lista das 27 UFs, em vez de aceitar qualquer coisa. A
 * exigência de 2 caracteres vale sempre, então nome de estado por extenso não passa
 * nem se o Arcaneum um dia devolver descrições em vez de siglas.
 */
export function isIssueStateValid(issueState: string, options: string[] = []): boolean {
  const value = (issueState || '').trim().toUpperCase();

  if (value.length !== 2) return false;

  const allowed = options.length
    ? options.map(option => option.trim().toUpperCase())
    : BRAZILIAN_UFS;

  return allowed.includes(value);
}

export function isCpfValid(cpf: string): boolean {
  const isCpfFormatValid = CPF_REGEX.test(cpf);

  if (!isCpfFormatValid) return false;

  const cpfNumbers = cpf.replace(/\D/g, '');

  if (cpfNumbers.length !== 11 || Array.from(cpfNumbers).every(char => char === cpfNumbers[0]))
    return false;

  let firstDigitSum = 0;
  for (let charPosition = 0; charPosition < 9; charPosition++) {
    firstDigitSum += Number(cpfNumbers.charAt(charPosition)) * (10 - charPosition);
  }

  let firstDigit = 11 - (firstDigitSum % 11);

  if (firstDigit === 10 || firstDigit === 11) firstDigit = 0;

  if (firstDigit !== Number(cpfNumbers.charAt(9))) return false;

  let secondDigitSum = 0;
  for (let charPosition = 0; charPosition < 10; charPosition++) {
    secondDigitSum += Number(cpfNumbers.charAt(charPosition)) * (11 - charPosition);
  }

  let secondDigit = 11 - (secondDigitSum % 11);

  if (secondDigit === 10 || secondDigit === 11) secondDigit = 0;

  if (secondDigit !== Number(cpfNumbers.charAt(10))) return false;

  return true;
}

export function isEmailValid(email: string): boolean {
  return EMAIL_REGEX.test(email);
}

export function isDateStringValid(date: string): boolean {
  return DATE_REGEX.test(date);
}

export function isMobilePhoneValid(mobilePhone: string): boolean {
  return MOBILE_PHONE_REGEX.test(mobilePhone);
}

export function isDateLessOrEqualToday(date: string): boolean {
  const todayDate = new Date();
  const inputDate = date.split('/');
  const parsedDate = new Date(Number(inputDate[2]), Number(inputDate[1]) - 1, Number(inputDate[0]));

  return todayDate >= parsedDate;
}

export function isDateMoreOrEqualBirthdate(date: string, birthDate: string) {
  const inputDate = date.split('/');
  const parsedDate = new Date(Number(inputDate[2]), Number(inputDate[1]) - 1, Number(inputDate[0]));
  const inputBirthDate = birthDate.split('/');
  const parsedBirthDate = new Date(
    Number(inputBirthDate[2]),
    Number(inputBirthDate[1]) - 1,
    Number(inputBirthDate[0]),
  );

  return parsedDate >= parsedBirthDate;
}

export function isFinancialSponsorAgeValid(dateStr: string): boolean {
  const [day, month, year] = dateStr.split('/').map(Number);
  const birthDate = new Date(year, month - 1, day);

  const today = new Date();
  const eighteenYearsAgo = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());

  return birthDate <= eighteenYearsAgo;
}
